// English source catalogue. Keys are stable identifiers; wording may change without code changes.
import { enM2 } from './en-m2.ts';
import { enM3 } from './en-m3.ts';
import { enOps } from './en-ops.ts';
import { enIdentity } from './en-identity.ts';
import { enContractors } from './en-contractors.ts';
import { enCredentials } from './en-credentials.ts';
import { enVehicles } from './en-vehicles.ts';
import { enPeople } from './en-people.ts';
import { enPrivacy } from './en-privacy.ts';
import { enVerification } from './en-verification.ts';
import { enCalendars } from './en-calendars.ts';
import { enAdapters } from './en-adapters.ts'; // ADR-025 (adapters & edge stream)
import { enAi } from './en-ai.ts';
import { enAiprov } from './en-aiprov.ts'; // external AI provider (aiprov stream, PD-600 – PD-606)
import { enOrgs } from './en-orgs.ts'; // people & organisations stream (ADR-028)
import { enRoles } from './en-roles.ts';
import { enSafety } from './en-safety.ts';
import { enReceptionGaps } from './en-reception-gaps.ts'; // reception remainders (PD-411 – PD-413)
import { enReceptionLookup } from './en-reception-lookup.ts'; // PB-06-02 lookup fields (PD-492 – PD-495)
import { enModel } from './en-model.ts'; // departments, HR mapping, site retention (PD-540 – PD-552)
import { enEmail } from './en-email.ts'; // email delivery settings (PD-580 – PD-589)
import { enOpsview } from './en-opsview.ts'; // operations views stream (PD-555 – PD-569)
import { enPortal } from './en-portal.ts'; // portal stream: receipts, UAE PASS signing, device printing (PD-510 – PD-515)
import { enRecurrence } from './en-recurrence.ts'; // recurrence & rooms stream (PD-470 – PD-479)
import { enOpsui } from './en-opsui.ts'; // operations-console stream (ADR-029)
import { enWorkflows } from './en-workflows.ts'; // visitor-type workflows stream (ADR-027)
import { enDocuments } from './en-documents.ts'; // documents stream (PB-05-07, PB-09-08, PB-07-09)
import { enMsg } from './en-msg.ts'; // messaging, correlation and reports stream (PD-525 – PD-539)
import { enPdf } from './en-pdf.ts'; // native PDF exports (PD-635 – PD-640)
import { enLdap } from './en-ldap.ts'; // LDAP / Active Directory directory stream (PD-590 – PD-597)
import { enPriv } from './en-priv.ts'; // privacy values stream: retroactive minimisation, legal bases (PD-610 – PD-624)
import { enClamav } from './en-clamav.ts'; // clamav stream: malware checks of uploads (PD-707, PD-645 – PD-654)
import { enDeploy } from './en-deploy.ts'; // deploy stream (PD-655 – PD-664)
import { enHardening } from './en-hardening.ts'; // hardening stream (PD-790 – PD-795)
import { enSettings } from './en-settings.ts'; // settings stream (PD-710, PD-665 – PD-689)
import { enDemoguard } from './en-demoguard.ts'; // demoguard stream: demo mode (PD-714, PD-720 – PD-739)
import { enDemodata } from './en-demodata.ts'; // demodata stream: demo account list (PD-740 – PD-759)
import { enDemosandbox } from './en-demosandbox.ts'; // demosandbox stream: per-browser demo sandbox (PD-763, PD-770 – PD-789)
import { enDemostaff } from './en-demostaff.ts'; // demostaff stream: staff and console demo sandboxes (PD-800, PD-810 – PD-829)

/**
 * PD-016: approved role copy is used verbatim except phrases that only made sense in the
 * prototype (persona names, "simulated", "demo"). Each override keeps the approved meaning.
 * apps/web/test/fidelity.test.ts checks every other role string against the registry.
 */
export const ROLE_COPY_OVERRIDES = {
  'role.host.intro': 'Invite visitors, keep plans current and follow the arrival journey. Your view includes only visits you host.',
  'role.approver.boundary': 'Requests you host cannot be approved by you; they need another assigned reviewer.',
  'role.auditor.intro': 'Review who did what and where uncertainty remains. Audit history is append-only.',
  'role.privacy.intro': 'Verify the requester, review the hold and track each storage outcome before closing a case.',
  'role.operator.intro': 'Inspect dependency health and reconcile uncertain credentials using authoritative read-back outcomes.',
  'role.operator.boundary': 'No routine visitor admission, approval, privacy deletion or user-management grant.',
  'role.operator.task3.body': 'Retry or dead-letter background work; transport health is not success.',
  'role.emergency.intro': 'Start a muster, review the captured roster and record individual observations. Passage is not an emergency service.',
  'role.emergency.primary': 'Open muster',
} as const;

export const en = {
  ...enRoles,
  ...ROLE_COPY_OVERRIDES,
  ...enM2,
  ...enM3,
  ...enOps,
  ...enIdentity,
  ...enContractors,
  ...enSafety,
  ...enCredentials,
  ...enVehicles,
  ...enPeople, // people & records stream (ADR-023)
  ...enPrivacy,
  ...enVerification,
  ...enCalendars,
  ...enAdapters,
  ...enDocuments,
  ...enAi, // AI add-ons stream (ADR-024)
  ...enAiprov, // external AI provider: provider key, connection test, personal-data rule
  ...enReceptionGaps, // reception remainders (PB-06-04, PB-06-12, PB-04-07)
  ...enReceptionLookup, // PB-06-02 lookup fields
  ...enModel, // PB-18-07 departments and HR mapping, PB-12-05 site retention
  ...enEmail, // PB-13-04 email delivery settings (email stream)
  ...enOpsview, // operations views: integration health, recovery evidence, visit access events, configuration promotion
  ...enPortal, // portal stream (PD-510 – PD-515)
  ...enRecurrence, // recurring visits and room booking policy (PB-04-02, PB-12-03)
  ...enOpsui, // operations-console stream (ADR-029)
  ...enWorkflows, // visitor-type workflows stream (ADR-027)
  ...enOrgs, // people & organisations stream (ADR-028)
  ...enMsg, // messaging, correlation and reports stream (PB-04-08, PB-09-14, PB-15-13, OR-11)
  ...enPdf, // native PDF exports (OR-11, PD-635 – PD-640)
  ...enLdap, // LDAP / Active Directory directory sync (PB-17-07, PB-03-09)
  ...enPriv, // privacy values stream (PB-16-03, H-04)
  ...enClamav, // clamav stream: malware checks of uploads (PD-707)
  ...enDeploy, // deploy stream: UAE pilot profile (PD-663)
  ...enHardening, // hardening stream: re-seal runs, identity-provider destinations (PD-791, PD-795)
  ...enSettings, // settings stream: installation settings in the platform console (PD-710)
  ...enDemoguard, // demoguard stream: demo mode, read-only and nothing sent (PD-714)
  ...enDemodata, // demodata stream: demo account list on the sign-in pages (PD-744, PD-749)
  ...enDemosandbox, // demosandbox stream: kiosk and pass answers appear to work in demo mode (PD-763)
  ...enDemostaff, // demostaff stream: staff and console changes appear to work in demo mode (PD-800)

  // ------------------------------------------------------------ common
  'app.name': 'Passage',
  'app.byline': 'by Scinops',
  'common.loading': 'Loading…',
  'common.cancel': 'Cancel',
  'common.save': 'Save changes',
  'common.saving': 'Saving…',
  'common.applying': 'Applying…',
  'common.close': 'Close dialog',
  'common.tryAgain': 'Try again',
  'common.reference': 'Reference: {id}',
  'common.reason': 'Reason',
  'common.reasonHint': 'Recorded in the administration audit.',
  'common.search': 'Search / filter',
  'common.loadMore': 'Load more',
  'common.loadOlder': 'Load older',
  'common.none': '—',
  'common.allSites': 'All sites',
  'common.organisationWide': 'Organisation-wide',
  'common.unavailableSite': 'Site outside your view',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.optional': '(optional)',
  'common.scrollHint': 'Scroll horizontally to see all columns →',
  'common.copy': 'Copy',
  'common.copied': 'Copied',

  'status.active': 'Active',
  'status.inactive': 'Inactive',
  'status.disabled': 'Disabled',
  'status.invited': 'Invited',
  'status.suspended': 'Suspended',
  'status.succeeded': 'Succeeded',
  'status.denied': 'Denied',
  'status.failed': 'Failed',
  'status.effective': 'Effective',
  'status.notEffective': 'Not effective',
  'status.revoked': 'Revoked',
  'status.noGrants': 'No grants',

  'error.title.forbidden': 'You do not have access to this in the current scope',
  'error.title.notFound': 'Not found',
  'error.title.network': 'Connection problem',
  'error.title.generic': 'Something went wrong',
  'error.requestFailed': 'The request failed.',
  'error.requestFailedStatus': 'The request failed (status {status}). Try again; if it keeps happening, contact support.',
  'error.notSaved': 'The change was not saved.',
  'error.network': 'Passage could not be reached, so the result is unknown. Check your connection and try again — a retry cannot apply the change twice.',
  'error.versionConflict.dialog': 'Someone changed this record after you opened it. Close this dialog to reload the latest version, then review your change.',

  // ------------------------------------------------------------ language
  'lang.label': 'Language',
  'lang.en': 'English',
  'lang.ar': 'العربية',
  'lang.unreviewed': 'Arabic text not yet reviewed',

  // ------------------------------------------------------------ authentication
  'auth.footer': 'Failed sign-in attempts and account recovery are recorded in the audit trail.',
  'auth.title': 'Sign in',
  'auth.lede': 'Use your configured organisation sign-in method.',
  'auth.ledeOrg': 'Use your configured {org} sign-in method.',
  'auth.unknownOrg.title': 'This address is not configured for an organisation.',
  'auth.unknownOrg.body': 'Check the link you were given or contact your administrator.',
  'auth.sso': 'Continue with organisation SSO',
  'auth.orLocal': 'Or use a product-managed account',
  'auth.missing': 'Enter your email or username and your password.',
  'auth.email': 'Email or username',
  'auth.emailPlaceholder': 'name@example.test',
  'auth.password': 'Password',
  'auth.passwordPlaceholder': 'Enter your password',
  'auth.submit': 'Sign in',
  'auth.submitting': 'Signing in…',
  'auth.recovery': 'Account recovery',
  'auth.fineprint1': 'MFA may be required by the configured role policy.',
  'auth.fineprint2': 'Recovery and failed attempts are audited.',
  'recovery.title': 'Account recovery',
  'recovery.lede': 'Ask your organisation administrator for a new activation link.',
  'recovery.body': 'An administrator confirms who you are and issues a single-use link to set a new password. Passage does not offer email-only recovery.',
  'recovery.back': 'Back to sign in',
  'activate.title': 'Set your password',
  'activate.lede': 'Choose a password for {email}.',
  'activate.checking': 'Checking your link…',
  'activate.invalid': 'This activation link is invalid, already used or expired. Ask your administrator for a new one.',
  'activate.password': 'New password',
  'activate.confirm': 'Confirm password',
  'activate.hint': 'At least {min} characters. A longer passphrase is stronger.',
  'activate.mismatch': 'The passwords do not match.',
  'activate.submit': 'Set password',
  'activate.done': 'Your password is set. You can now sign in.',

  // ------------------------------------------------------------ shell and navigation
  'shell.skip': 'Skip to workspace',
  'shell.yourResponsibilities': 'Your responsibilities',
  'shell.workspace': 'Workspace',
  'shell.footer': 'Navigation follows your granted responsibilities.',
  'shell.noResponsibilities': 'No responsibilities granted',
  'shell.openNav': 'Open navigation',
  'shell.workingSite': 'Working site',
  'shell.inactiveSuffix': ' (inactive)',
  'shell.localTimeAt': 'Local time at {site}',
  'shell.signOut': 'Sign out',
  'shell.envDev': 'LOCAL DEVELOPMENT',
  'shell.envDevNote': 'Synthetic data only · no live integrations connected',
  'shell.planned': 'Planned',
  'nav.home': 'My workspace',
  'nav.users': 'Users & permissions',
  'nav.sites': 'Sites & rooms',
  'nav.auditlog': 'Audit trail',
  'nav.policies': 'Policies & workflows',
  'nav.visits': 'Visits & invitations',
  'nav.approvals': 'Approvals',
  'nav.reception': 'Reception',
  'nav.communications': 'Communications',
  'nav.deliveries': 'Deliveries',
  'nav.contractors': 'Contractors & permits',
  'nav.vehicles': 'Vehicles & gates',
  'nav.credentials': 'Badges & access',
  'nav.integrations': 'Integrations & devices',
  'nav.safety': 'Occupancy & muster',
  'nav.summary': 'Aggregate reports',
  'nav.privacy': 'Privacy & retention',
  'nav.operations': 'Platform operations',
  'nav.security': 'Security policy',

  // ------------------------------------------------------------ home and placeholders
  'home.eyebrow': 'Passage / {name}',
  'home.none.title': 'No responsibilities granted yet',
  'home.none.body': 'Your account is active but has no granted responsibilities, so no workspace is available. Ask your organisation administrator to grant the access you need.',
  'home.openWorkspace': 'Open workspace →',
  'home.notYet': 'Not available yet',
  'home.boundary': 'Responsibility boundary',
  'planned.title': 'Not implemented in this build',
  'planned.body': 'This workspace is scheduled for milestone {milestone} (module {modules}). It is shown so the approved navigation stays intact; no simulated records or actions are offered here.',
  'notFound.title': 'Not found',
  'notFound.body': 'This page does not exist or is not available to you.',
  'notFound.back': 'Return to your workspace',
  'denied.title': 'Not available for your role',
  'denied.body': 'Your current responsibilities do not include this workspace. If you need it, ask your organisation administrator.',

  // ------------------------------------------------------------ sites and locations
  'sites.title': 'Sites, rooms & locations',
  'sites.intro': 'Site-local configuration with an explicit effective scope.',
  'sites.none.title': 'No sites in your scope',
  'sites.none.body': 'You have no site-level access. An organisation administrator can grant it.',
  'sites.loading': 'Loading site…',
  'sites.scope': 'Scope: {site}',
  'sites.scopeBody': 'Only locations in this site are shown. Local time {time} on {date} ({zone}, {offset}). Cross-site administration needs a separate grant.',
  'sites.add': 'Add site',
  'sites.addTitle': 'Add a site',
  'sites.addSub': 'A site has its own time zone, locations and operating rules.',
  'sites.field.code': 'Site code',
  'sites.field.codeHint': 'Capital letters, digits or hyphens, for example DXB-HQ.',
  'sites.deactivate': 'Deactivate site…',
  'sites.reactivate': 'Reactivate site…',
  'sites.deactivateTitle': 'Deactivate {name}?',
  'sites.deactivateSub': 'All active locations must be deactivated first. History is kept.',
  'sites.reactivateTitle': 'Reactivate {name}?',
  'sites.reactivateSub': 'Checked against your organisation’s active-site allowance.',
  'sites.tab.locations': 'Rooms & locations',
  'sites.tab.settings': 'Site settings',
  'sites.tab.branding': 'Branding',
  'brand.title': 'Site branding',
  'brand.sub': 'Shown to visitors on invitations, passes and the kiosk for this site.',
  'brand.logo': 'Logo',
  'brand.logoHint': 'PNG, JPEG, WebP or a plain SVG, up to 512 KB. SVG with scripts or external references is refused.',
  'brand.logoNone': 'No logo uploaded.',
  'brand.upload': 'Upload logo',
  'brand.remove': 'Remove logo',
  'brand.colour': 'Brand colour',
  'brand.colourHint': 'Used for buttons with white text; must reach a contrast of at least 4.5:1.',
  'brand.contrast': 'Contrast with white text: {ratio}:1',
  'brand.contrastOk': 'Readable',
  'brand.contrastLow': 'Too light',
  'brand.contact': 'Contact line',
  'brand.contactHint': 'For example the reception phone number.',
  'brand.preview': 'Preview',
  'brand.previewBadge': 'Visitor pass preview',
  'brand.previewVisitor': 'Synthetic visitor',
  'brand.save': 'Save branding',
  'brand.saved': 'Branding saved (version {version}).',
  'brand.scanNote': 'Uploads are checked for type, size and active content. Malware scanning is not connected in this build.',
  'loc.field.picture': 'Picture',
  'loc.field.pictureHint': 'PNG, JPEG or WebP, up to 5 MB.',
  'loc.picture.upload': 'Upload picture',
  'loc.picture.remove': 'Remove picture',
  'loc.picture.alt': 'Picture of {name}',
  'sites.tabsLabel': 'Site sections',
  'kind.campus': 'Campus',
  'kind.building': 'Building',
  'kind.floor': 'Floor',
  'kind.zone': 'Zone',
  'kind.gate': 'Gate',
  'kind.room': 'Room',
  'kind.desk': 'Reception desk',
  'kind.parking': 'Parking',
  'kind.other': 'Other destination',
  'loc.register': 'Location register',
  'loc.registerSub': 'Search and selection remain scoped to this site.',
  'loc.add': '+ Add location',
  'loc.searchPlaceholder': 'Search locations in current scope',
  'loc.loading': 'Loading locations…',
  'loc.none.filtered': 'No matching locations',
  'loc.none': 'No locations yet',
  'loc.none.filteredBody': 'Change the search to see more of this site.',
  'loc.none.createBody': 'Add the first building, gate or room for this site.',
  'loc.none.readBody': 'No locations have been configured for this site.',
  'loc.tableLabel': 'Locations table',
  'loc.col.location': 'Location',
  'loc.col.type': 'Type / parent',
  'loc.col.capacity': 'Capacity',
  'loc.col.status': 'Status',
  'loc.col.action': 'Action',
  'loc.parentFallback': 'Parent',
  'loc.edit': 'Edit',
  'loc.editLabel': 'Edit {name}',
  'loc.deactivate': 'Deactivate…',
  'loc.reactivate': 'Reactivate…',
  'loc.deactivateLabel': 'Review deactivation of {name}',
  'loc.reactivateLabel': 'Reactivate {name}',
  'loc.count': { one: '{count} location in {site}', other: '{count} locations in {site}' },
  'loc.truncated': ' (list truncated — refine the search)',
  'loc.before.title': 'Before deactivation',
  'loc.before.deps': 'Check dependencies',
  'loc.before.depsBody': 'Active child locations, future visits and access-zone mappings block deactivation until resolved. Rules that name the location (approver scopes, arrival guidance) are listed and need confirmation.',
  'loc.before.impact': 'Preview impact',
  'loc.before.impactBody': 'Affected records are shown before any change is applied; a concurrent change is detected at apply time.',
  'loc.before.recovery': 'Recovery',
  'loc.before.recoveryBody': 'Locations are never deleted. Deactivation keeps history and can be reversed when the parent is active.',
  'loc.dialog.add': 'Add location',
  'loc.dialog.edit': 'Edit {name}',
  'loc.dialog.savedTo': 'Saved to {site} only.',
  'loc.field.type': 'Type',
  'loc.field.parent': 'Parent location',
  'loc.field.parentHint': 'Leave empty to attach directly to the site.',
  'loc.field.parentSite': '{site} (site)',
  'loc.field.name': 'Name',
  'loc.field.code': 'Code (optional)',
  'loc.field.codeHint': 'Letters, digits, dots, underscores or hyphens.',
  'loc.field.capacity': 'Capacity (optional)',
  'loc.field.capacityHint': 'People the space can hold.',
  'loc.submit.add': 'Add location',
  'loc.status.deactivateTitle': 'Deactivate {name}?',
  'loc.status.reactivateTitle': 'Reactivate {name}?',
  'loc.status.sub': 'Review the impact before applying.',
  'loc.status.checking': 'Checking dependencies…',
  'loc.status.blocked': { one: 'Blocked: {count} active location inside it', other: 'Blocked: {count} active locations inside it' },
  'loc.status.blockedBody': 'Deactivate or move these first:',
  'loc.status.clear': 'No active child locations.',
  'loc.status.unchecked': 'Not yet checked (modules not built): {list}.',
  'loc.status.reactivateNote': 'The parent location must be active. History is retained either way.',
  'loc.status.submitDeactivate': 'Deactivate location',
  'loc.status.submitReactivate': 'Reactivate location',
  'settings.title': 'Site settings',
  'settings.meta': 'Code {code} · version {version}',
  'settings.editable': 'You can edit this site.',
  'settings.readOnly': 'Read-only for your role.',
  'settings.conflict': 'Someone saved this site after you opened it.',
  'settings.reload': 'Reload latest version',
  'settings.conflictAfter': 'Your unsaved edits will be replaced; review them again afterwards.',
  'settings.saved': 'Saved. Site is now at version {version}.',
  'settings.name': 'Site name',
  'settings.tz': 'IANA time zone',
  'settings.tzHint': 'Schedules keep their recorded instants if this changes.',
  'settings.country': 'Country (ISO code)',
  'settings.phone': 'Reception phone',
  'settings.email': 'Reception email',
  'settings.address': 'Address',
  'settings.instructions': 'Visitor instructions',
  'settings.tzReview': 'Time-zone change needs an impact review',
  'settings.tzReviewBody': 'Existing records keep their recorded UTC instants; they will display in the new zone. Future visits cannot be counted yet because visit planning is not built.',
  'settings.tzAck': 'I have reviewed the impact of changing from {from} to {to}.',
  'settings.submit': 'Save site settings',

  // ------------------------------------------------------------ users and access
  'users.eyebrow': 'Passage / Administration',
  'users.title': 'Users & permissions',
  'users.intro': 'Grant the minimum scope needed for each responsibility.',
  'users.note.title': 'Roles do not imply document access',
  'users.note.body': 'Identity previews, exports, overrides and other sensitive powers come only from specialist roles, which only authorised administrators can assign.',
  'users.panel': 'People with access',
  'users.panelSub': 'Accounts in your organisation.',
  'users.searchPlaceholder': 'Search loaded accounts',
  'users.loading': 'Loading users…',
  'users.none': 'No matching accounts',
  'users.tableLabel': 'Users table',
  'users.col.user': 'User',
  'users.col.responsibilities': 'Responsibilities',
  'users.col.state': 'State',
  'users.col.action': 'Action',
  'users.preview': 'Preview access',
  'users.previewLabel': 'Preview access for {name}',
  'users.manage': 'Manage',
  'users.manageLabel': 'Manage {name}',
  'users.loaded': { one: '{count} account loaded', other: '{count} accounts loaded' },
  'users.review': 'Permission review',
  'users.reviewHint': 'Choose “Preview access” to see what a person can do, computed by the server from their current grants.',
  'users.create': 'Add user',
  'access.user': 'User',
  'access.orgWide': 'Organisation-wide',
  'access.nothingOrgWide': 'Nothing organisation-wide.',
  'access.notGranted': 'Not granted',
  'access.notGrantedBody': 'Anything not listed is denied by default, including sensitive capabilities.',
  'access.hiddenScopes': 'Grants on sites you do not administer are not shown.',
  'user.eyebrow': 'Passage / Users & permissions',
  'user.loading': 'Loading user…',
  'user.grant': 'Grant responsibility',
  'user.grantSpecialist': 'Assign specialist role',
  'user.revokeSessions': 'Revoke sessions ({count})',
  'user.deactivate': 'Deactivate…',
  'user.resetPassword': 'Issue activation link…',
  'user.self': 'You cannot change your own access. Another administrator must do it.',
  'user.grants': 'Grants',
  'user.grantsSub': 'Current and past grants. Revoked and expired grants are kept for accountability.',
  'user.grantsLabel': 'Grants table',
  'user.col.responsibility': 'Responsibility',
  'user.col.scope': 'Scope',
  'user.col.validity': 'Validity',
  'user.col.state': 'State',
  'user.col.action': 'Action',
  'user.noGrants': 'No grants have been issued to this user.',
  'user.kind.experience': 'Experience',
  'user.kind.capability': 'Capability',
  'user.kind.specialist': 'Specialist role',
  'user.from': 'From {date}',
  'user.until': 'Until {date}',
  'user.noEnd': 'No end date',
  'user.revoke': 'Revoke…',
  'user.revokeLabel': 'Revoke {role} for {scope}',
  'user.effective': 'Effective access',
  'user.revokeTitle': 'Revoke {role}?',
  'user.revokeSub': '{name} · {scope}. Takes effect on their next request.',
  'user.revokeConfirm': 'Revoke grant',
  'user.deactivateTitle': 'Deactivate {name}?',
  'user.deactivateSub': 'Their sessions end immediately. Grants and history are kept.',
  'user.deactivateConfirm': 'Deactivate user',
  'user.sessionsTitle': 'Revoke all sessions?',
  'user.sessionsSub': '{name} will need to sign in again on every device.',
  'user.sessionsConfirm': 'Revoke sessions',
  'grant.title': 'Grant a responsibility to {name}',
  'grant.titleSpecialist': 'Assign a specialist role to {name}',
  'grant.sub': 'Takes effect on their next request. Recorded in the administration audit.',
  'grant.experience': 'Experience',
  'grant.specialist': 'Specialist role',
  'grant.scope': 'Scope',
  'grant.scopeOrg': 'All sites (organisation-wide)',
  'grant.scopeSiteOnly': 'This experience is always site-specific.',
  'grant.ends': 'Ends',
  'grant.endsOptional': 'Ends (optional)',
  'grant.endsHint': "Your device's local time.",
  'grant.endsRequired': 'Required: access you grant as a site administrator must end within {days} days.',
  'grant.submit': 'Grant responsibility',
  'grant.submitSpecialist': 'Assign specialist role',
  'grant.submitting': 'Granting…',
  'activation.title': 'Activation link for {name}',
  'activation.body': 'Give this single-use link to {name} through a trusted channel. It expires {expires}. It is shown only once.',
  'activation.issueTitle': 'Issue a new activation link?',
  'activation.issueSub': 'Any earlier link stops working and {name} is signed out everywhere.',
  'activation.issueConfirm': 'Issue link',
  'createUser.title': 'Add a user',
  'createUser.sub': 'Passage creates the account and a single-use activation link. Grant responsibilities afterwards.',
  'createUser.email': 'Email',
  'createUser.name': 'Display name',
  'createUser.submit': 'Create user',

  // ------------------------------------------------------------ audit
  'audit.eyebrow': 'Passage / Auditor',
  'audit.title': 'Audit trail',
  'audit.intro': 'Follow the evidence, without changing it. Records are append-only.',
  'audit.panel': 'Events in your scope',
  'audit.panelSub': 'Newest first. Before/after values appear only with the separate sensitive-audit capability.',
  'audit.loading': 'Loading audit events…',
  'audit.none': 'No audit events in your scope yet',
  'audit.tableLabel': 'Audit events',
  'audit.col.when': 'When (your device)',
  'audit.col.action': 'Action',
  'audit.col.where': 'Where',
  'audit.col.outcome': 'Outcome',
  'audit.col.reason': 'Reason / reference',
  'audit.organisation': 'Organisation',
  'audit.loaded': { one: '{count} event loaded', other: '{count} events loaded' },

  // ------------------------------------------------------------ security policy (PD-002)
  'policy.title': 'Security policy',
  'policy.intro': 'Effective sign-in and session rules for your organisation. Only a system administrator can change them.',
  'policy.source.platform': 'Platform default',
  'policy.source.organisation': 'Organisation override',
  'policy.sessionIdle': 'Idle session timeout (minutes)',
  'policy.sessionAbsolute': 'Maximum session length (hours)',
  'policy.maxFailures': 'Failed sign-ins before lock (per account)',
  'policy.window': 'Failure counting window (minutes)',
  'policy.lock': 'Lock duration (minutes)',
  'policy.networkFailures': 'Failed sign-ins before lock (per network)',
  'policy.passwordMin': 'Minimum password length',
  'policy.activationHours': 'Activation link validity (hours)',
  'policy.siteGrantMaxDays': 'Longest access a site administrator can grant (days)',
  'policy.version': 'Policy version {version}',

  // ------------------------------------------------------------ platform console (PD-003)
  'platform.eyebrow': 'Passage / System administration',
  'platform.title': 'System administration',
  'platform.signInLede': 'System administrator sign-in. Organisation users sign in at their organisation address.',
  'platform.nav.organisations': 'Organisations',
  'platform.nav.policy': 'Platform security policy',
  'platform.orgs.title': 'Organisations',
  'platform.orgs.intro': 'Create organisations, their sign-in addresses and entitlements. Organisation data stays inside each organisation.',
  'platform.orgs.create': 'Add organisation',
  'platform.orgs.col.name': 'Organisation',
  'platform.orgs.col.addresses': 'Sign-in addresses',
  'platform.orgs.col.sites': 'Active sites',
  'platform.orgs.col.entitlements': 'Entitlements',
  'platform.orgs.col.status': 'Status',
  'platform.org.slug': 'Identifier',
  'platform.org.name': 'Organisation name',
  'platform.org.hostname': 'Sign-in address (host name)',
  'platform.org.multiSite': 'Multi-site enabled',
  'platform.org.maxSites': 'Maximum active sites',
  'platform.org.maxSitesHint': 'Leave empty for no limit.',
  'platform.org.firstAdminEmail': 'First administrator email',
  'platform.org.firstAdminName': 'First administrator name',
  'platform.org.entitlements': 'Entitlements',
  'platform.org.policy': 'Organisation security policy',
  'platform.org.policyInherit': 'Uses the platform default.',
  'platform.org.policyOverride': 'Override platform default',
  'platform.org.policyClear': 'Return to platform default',
  'platform.org.addAdmin': 'Add administrator',
  'platform.org.adminEmail': 'Administrator email',
  'platform.org.adminName': 'Administrator name',
  'platform.org.users': 'Administrators',
  'platform.org.suspend': 'Suspend organisation',
  'platform.org.reactivate': 'Reactivate organisation',
  'platform.policy.title': 'Platform security policy',
  'platform.policy.intro': 'Default for every organisation without an override. Changes apply to the next sign-in; existing sessions keep their expiry.',

  // ------------------------------------------------------------ specialist roles (PD-004)
  'specialist.identity-verifier': 'Identity verifier',
  'specialist.credential-issuer': 'Credential issuer',
  'specialist.privacy-authority': 'Privacy authority',
  'specialist.report-exporter': 'Report exporter',
  'specialist.sensitive-auditor': 'Sensitive audit reader',
  'specialist.muster-exporter': 'Muster roster exporter',
  'specialist.approval-coordinator': 'Approval coordinator',
  'specialist.delegated-host': 'Delegated host',
  'specialist.company-suspension': 'Contractor suspension authority',

  // ------------------------------------------------------------ multi-factor authentication (PD-020)
  'mfa.title': 'Two-step verification',
  'mfa.verify.lede': 'Enter the 6-digit code from your authenticator app.',
  'mfa.verify.code': 'Authentication code',
  'mfa.verify.useRecovery': 'Use a recovery code instead',
  'mfa.verify.useCode': 'Use an authenticator code instead',
  'mfa.verify.recovery': 'Recovery code',
  'mfa.verify.submit': 'Verify',
  'mfa.enrol.title': 'Set up two-step verification',
  'mfa.enrol.lede': 'Your role requires a second step. Scan the code with an authenticator app, then enter the 6-digit code it shows.',
  'mfa.enrol.ledeOptional': 'Scan the code with an authenticator app, then enter the 6-digit code it shows.',
  'mfa.enrol.manual': 'Or enter this key manually',
  'mfa.enrol.submit': 'Confirm and turn on',
  'mfa.codes.title': 'Save your recovery codes',
  'mfa.codes.body': 'Each code works once if you lose your device. They are shown only now — store them somewhere safe.',
  'mfa.codes.done': 'I have saved them',
  'mfa.account.title': 'Account security',
  'mfa.account.status.on': 'Two-step verification is on.',
  'mfa.account.status.off': 'Two-step verification is off.',
  'mfa.account.required': 'Required for your role by your organisation’s policy.',
  'mfa.account.turnOn': 'Turn on two-step verification',
  'mfa.account.turnOff': 'Turn off',
  'mfa.account.newCodes': 'New recovery codes',
  'mfa.account.confirmCode': 'Enter a current code to confirm',
  'mfa.reset': 'Reset two-step verification…',
  'mfa.resetTitle': 'Reset two-step verification for {name}?',
  'mfa.resetSub': 'Use when a device is lost. Their sessions end and they set up a new factor at next sign-in.',
  'mfa.resetConfirm': 'Reset',
  'policy.mfaRequirement': 'Two-step verification required for',
  'policy.mfa.none': 'Nobody (optional for everyone)',
  'policy.mfa.administrators': 'Administrators and specialist roles',
  'policy.mfa.all': 'Everyone',
  // ------------------------------------------------------------ API messages (PD-015)
  'api.unauthenticated': 'Sign in to continue.',
  'api.notFound': 'The requested record was not found.',
  'api.forbidden': 'You do not have permission for this action in the current scope.',
  'api.validation': 'Some details need attention.',
  'api.versionConflict': 'This record changed since you loaded it. Reload and review before saving again.',
  'api.unknownOrganisation': 'This address is not configured for an organisation.',
  'api.originRejected': 'This request origin is not allowed.',
  'api.csrfRejected': 'The request could not be verified. Reload the page and try again.',
  'api.idempotencyRequired': 'This command needs an Idempotency-Key header so retries cannot repeat it.',
  'api.idempotencyInvalid': 'Idempotency-Key is not valid.',
  'api.idempotencyReused': 'This Idempotency-Key was already used for a different request.',
  'api.invalidCredentials': 'The sign-in details were not accepted. Check them and try again.',
  'api.rateLimited': 'Too many requests in a short time. Wait a moment, then try again.',
  'api.signInRateLimited': 'Too many sign-in attempts. Wait before trying again.',
  'api.unsupportedMediaType': 'Send JSON request bodies.',
  'api.unreadable': 'The request could not be read.',
  'api.methodNotAllowed': 'This action is not available here.',
  'api.requestConflict': 'This request conflicts with the current state. Reload and try again.',
  'api.requestRejected': 'The request was refused.',
  'api.internal': 'Something went wrong. Quote the reference if you contact support.',
  'api.selfAccess': 'You cannot change your own access.',
  'api.entitlementMultiSite': 'Your organisation needs the multi-site entitlement to add another active site.',
  'api.siteLimit': 'Your organisation has reached its limit of {max} active sites.',
  'api.tzImpact': 'Changing the site time zone needs an impact review. Existing records keep their recorded instants.',
  'api.siteInactive': 'This site is inactive.',
  'api.alreadyStatus': 'This record is already {status}.',
  'api.locationChildren': 'Deactivate or move the active locations inside it first.',
  'api.siteHasLocations': 'Deactivate the active top-level locations of this site first.',
  'api.mfaInvalid': 'That code was not accepted. Check your authenticator app and try again.',
  'api.mfaChallengeExpired': 'The sign-in step expired. Sign in again.',
  'api.mfaRequiredByPolicy': 'Your organisation requires multi-factor authentication for your role, so it cannot be turned off.',
  'api.payloadTooLarge': 'The upload is too large.',
  'api.userDisabled': 'This user is disabled.',
  'api.grantExists': 'The user already holds this grant in this scope.',
  'api.grantRevoked': 'This grant is already revoked.',
  'api.lastAdmin': 'This would leave the organisation without an effective administrator. Grant another administrator first.',
  'api.organisationSuspended': 'This organisation is suspended.',
  'api.userExists': 'A user with this email already exists.',
  'api.organisationExists': 'An organisation with this identifier or address already exists.',

  // ------------------------------------------------------------ validation (field messages)
  'v.required': 'Required.',
  'v.tooLong': 'At most {max} characters.',
  'v.tooShort': 'At least {min} characters.',
  'v.invalid': 'This value is not valid.',
  'v.mfaBelowFloor': 'An organisation can require more multi-factor authentication than the platform minimum, never less.',
  'v.number': 'Enter a whole number between {min} and {max}.',
  'v.email': 'Enter a valid email address.',
  'v.siteCode': 'Use 1–32 capital letters, digits or hyphens.',
  'v.locationCode': 'Use 1–32 letters, digits, dots, underscores or hyphens.',
  'v.countryCode': 'Use a two-letter country code.',
  'v.timeZone': 'Choose a valid IANA time zone, for example Asia/Dubai.',
  'v.time': 'Use 24-hour time, for example 08:30.',
  'v.cursor': 'Invalid cursor.',
  'v.siteCodeTaken': 'Another site already uses this code.',
  'v.locationCodeTaken': 'Another location in this site already uses this code.',
  'v.parentRequired': 'A {kind} must sit inside a parent location.',
  'v.parentSelf': 'A location cannot be its own parent.',
  'v.parentNotInSite': 'Choose a parent location in this site.',
  'v.parentKind': 'A {kind} cannot be placed under a {parent}.',
  'v.parentInactive': 'The parent location is inactive.',
  'v.cycle': 'This move would place the location inside itself.',
  'v.capacityNotApplicable': 'Capacity does not apply to this type of location.',
  'v.grantUnknown': 'Choose a known experience, specialist role or capability.',
  'v.grantNotStaff': 'Visitor and kiosk experiences are session types, not staff grants.',
  'v.grantSiteRequired': 'This experience must be granted for a specific site.',
  'v.grantHumanOnly': 'Automation identities cannot hold approval, credential, gate or presence powers.',
  'v.grantOrgOnly': 'This capability applies organisation-wide; do not choose a site.',
  'v.grantSiteNotFound': 'Choose a site in this organisation.',
  'v.grantFuture': 'The end must be in the future.',
  'v.grantSiteAdminScope': 'You can grant access only on sites you administer.',
  'v.grantSiteAdminCode': 'Site administrators cannot grant this responsibility.',
  'v.grantEndRequired': 'Access you grant must end within {days} days.',
  'v.passwordShort': 'Use at least {min} characters.',
  'v.passwordEmail': 'The password must not be your email address.',
  'v.slug': 'Use 1–64 lowercase letters, digits or hyphens.',
  'v.hostname': 'Enter a host name such as example.passage.test.',
  'v.otp': 'Enter the 6-digit code from your authenticator app.',
  'v.colour': 'Use a colour in the form #rrggbb.',
  'v.contrast': 'This colour is too light for white text (contrast {ratio}:1; at least 4.5:1 is required).',
  'v.uploadEmpty': 'The file is empty.',
  'v.uploadTooLarge': 'The file is larger than {max}.',
  'v.uploadType': 'Use a PNG, JPEG or WebP image{svg}.',
  'v.uploadMismatch': 'The file content does not match its type.',
  'v.uploadUnsafeSvg': 'This SVG contains scripts, external references or other active content and cannot be used.',
  'v.uploadMalformed': 'The file could not be read as an image.',
  'v.range': 'Enter a value between {min} and {max}.',
} as const;
